import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/hooks/use-toast";
import { Linkedin, Instagram, Send, Mail, Sparkles } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import AnimatedSection from "@/components/AnimatedSection";
import HCaptcha from "@hcaptcha/react-hcaptcha";

const WEB3FORMS_API_KEY = "a4b7e66c-2391-4b31-b496-929a45c6aee9";
const HCAPTCHA_SITE_KEY = "50b2fe65-b00b-4b9e-ad62-3ba471098be2"; // hCaptcha public site key for Web3Forms

const Contact = () => {
  const { t, language } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaRef = useRef<HCaptcha>(null);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    company: "",
    jobTitle: "",
    message: "",
    consent: false,
  });

  const handleCaptchaVerify = (token: string) => {
    setCaptchaToken(token);
  };

  const handleCaptchaExpire = () => {
    setCaptchaToken(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation côté client
    if (formData.message.trim().length < 10) {
      toast({
        title: language === "fr" ? "Message trop court" : "Message too short",
        description: language === "fr" 
          ? "Votre message doit contenir au moins 10 caractères." 
          : "Your message must be at least 10 characters.",
        variant: "destructive",
      });
      return;
    }

    if (!formData.consent) {
      toast({
        title: language === "fr" ? "Consentement requis" : "Consent required",
        description: language === "fr" 
          ? "Veuillez accepter d'être contacté par Localove." 
          : "Please agree to be contacted by Localove.",
        variant: "destructive",
      });
      return;
    }

    if (!captchaToken) {
      toast({
        title: language === "fr" ? "Vérification requise" : "Verification required",
        description: language === "fr" 
          ? "Veuillez compléter la vérification anti-spam." 
          : "Please complete the anti-spam verification.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Envoi via Web3Forms pour recevoir par email
      const web3FormData = {
        access_key: WEB3FORMS_API_KEY,
        subject: `Nouvelle demande Localove - ${formData.company}`,
        from_name: `${formData.firstName} ${formData.lastName}`,
        "h-captcha-response": captchaToken,
        name: `${formData.firstName} ${formData.lastName}`,
        email: formData.email,
        phone: formData.phone || "Non renseigné",
        company: formData.company,
        job_title: formData.jobTitle || "Non renseigné",
        message: formData.message,
      };

      const web3Response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(web3FormData),
      });

      const web3Result = await web3Response.json();

      if (!web3Result.success) {
        throw new Error(web3Result.message || "Erreur Web3Forms");
      }

      // Également sauvegarder dans Supabase pour le backoffice
      const { error: supabaseError } = await supabase.functions.invoke('submit-contact', {
        body: {
          first_name: formData.firstName.trim(),
          last_name: formData.lastName.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim() || null,
          company: formData.company.trim(),
          job_title: formData.jobTitle.trim() || null,
          message: formData.message.trim(),
          consent: formData.consent,
        },
      });

      if (supabaseError) {
        console.warn("Supabase backup failed:", supabaseError);
      }

      toast({
        title: language === "fr" ? "Demande envoyée !" : "Request sent!",
        description: language === "fr" 
          ? "Nous avons bien reçu votre demande d'audit. Notre équipe vous contactera sous 24h."
          : "We received your audit request. Our team will contact you within 24h.",
      });
      
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        company: "",
        jobTitle: "",
        message: "",
        consent: false,
      });
      setCaptchaToken(null);
      captchaRef.current?.resetCaptcha();
    } catch (error) {
      console.error("Error submitting contact form:", error);
      toast({
        title: language === "fr" ? "Erreur" : "Error",
        description: error instanceof Error ? error.message : (language === "fr" ? "Une erreur s'est produite. Veuillez réessayer." : "An error occurred. Please try again."),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="pt-4 lg:pt-6 pb-12 lg:pb-16 bg-slate-50/50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="relative rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-lg">
            <div className="absolute inset-0 bg-gradient-to-br from-rose-50/30 via-transparent to-pink-50/30" />
            
            <div className="relative z-10 p-6 lg:p-8">
              {/* Header compact */}
              <div className="text-center mb-6">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-rose-100 border border-rose-200 mb-3">
                  <Sparkles className="w-4 h-4 text-rose-600" />
                  <span className="text-sm font-medium text-rose-700">
                    {language === "fr" ? "Gratuit" : "Free"}
                  </span>
                </div>
                
                <h2 className="text-2xl lg:text-3xl font-display font-bold text-slate-800 mb-2">
                  {language === "fr" ? "Commencez " : "Start "}
                  <span className="bg-gradient-to-r from-rose-400 via-pink-500 to-rose-400 bg-clip-text text-transparent animate-gradient-shine">
                    {language === "fr" ? "votre transformation" : "your transformation"}
                  </span>
                </h2>
                <p className="text-sm text-slate-500">{t("contact.subtitle")}</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="firstName" className="text-slate-700 text-sm">
                      {t("contact.firstname")} <span className="text-rose-500">*</span>
                    </Label>
                    <Input
                      id="firstName"
                      required
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      className="bg-white border-slate-200 text-slate-800 placeholder:text-slate-400 focus:border-rose-400 focus:ring-rose-400/20 h-10"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="lastName" className="text-slate-700 text-sm">
                      {t("contact.name")} <span className="text-rose-500">*</span>
                    </Label>
                    <Input
                      id="lastName"
                      required
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      className="bg-white border-slate-200 text-slate-800 placeholder:text-slate-400 focus:border-rose-400 focus:ring-rose-400/20 h-10"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="email" className="text-slate-700 text-sm">
                      {t("contact.email")} <span className="text-rose-500">*</span>
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="bg-white border-slate-200 text-slate-800 placeholder:text-slate-400 focus:border-rose-400 focus:ring-rose-400/20 h-10"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="company" className="text-slate-700 text-sm">
                      {t("contact.company")} <span className="text-rose-500">*</span>
                    </Label>
                    <Input
                      id="company"
                      required
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      className="bg-white border-slate-200 text-slate-800 placeholder:text-slate-400 focus:border-rose-400 focus:ring-rose-400/20 h-10"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="message" className="text-slate-700 text-sm">
                    {t("contact.message")} <span className="text-rose-500">*</span>
                  </Label>
                  <Textarea
                    id="message"
                    required
                    rows={3}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="bg-white border-slate-200 text-slate-800 placeholder:text-slate-400 focus:border-rose-400 focus:ring-rose-400/20 resize-none"
                    placeholder={t("contact.message.placeholder")}
                  />
                </div>

                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="consent"
                    checked={formData.consent}
                    onCheckedChange={(checked) => setFormData({ ...formData, consent: checked as boolean })}
                    className="border-slate-300 data-[state=checked]:bg-rose-500 data-[state=checked]:border-rose-500 mt-0.5"
                  />
                  <Label htmlFor="consent" className="text-xs text-slate-500 cursor-pointer leading-relaxed">
                    {t("contact.rgpd")}
                  </Label>
                </div>

                {/* hCaptcha with custom styling */}
                <div className="flex justify-center">
                  <div className="relative rounded-xl overflow-hidden border border-slate-200 bg-gradient-to-r from-rose-50/50 to-pink-50/50 p-3 shadow-sm">
                    <div className="absolute inset-0 bg-gradient-to-r from-rose-100/20 to-pink-100/20 pointer-events-none" />
                    <HCaptcha
                      ref={captchaRef}
                      sitekey={HCAPTCHA_SITE_KEY}
                      onVerify={handleCaptchaVerify}
                      onExpire={handleCaptchaExpire}
                      theme="light"
                      size="normal"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  size="lg"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-400 hover:to-pink-400 text-white font-semibold py-5 rounded-full shadow-xl shadow-rose-500/25 hover:shadow-rose-500/40 transition-all duration-300 hover:scale-[1.02] group"
                >
                  {isSubmitting ? (
                    <span className="animate-pulse">...</span>
                  ) : (
                    <>
                      {t("contact.submit")}
                      <Send className="ml-2 h-5 w-5 group-hover:translate-x-2 group-hover:-translate-y-1 transition-transform duration-300" />
                    </>
                  )}
                </Button>
              </form>

              <div className="mt-6 pt-4 border-t border-slate-200 flex flex-wrap items-center justify-center gap-4 text-sm">
                <a href="mailto:contact@localove.fr" className="text-slate-500 hover:text-rose-600 transition-colors flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  contact@localove.fr
                </a>
                <a href="https://www.linkedin.com/company/localove/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="w-8 h-8 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-500 hover:text-rose-500 hover:border-rose-300 transition-all">
                  <Linkedin className="h-4 w-4" />
                </a>
                <a href="https://www.instagram.com/localove.fr" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-8 h-8 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-500 hover:text-pink-500 hover:border-pink-300 transition-all">
                  <Instagram className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
